import React, { useState, useEffect } from "react";
import { Bell, Save, CheckCircle2, RefreshCw, AlertCircle } from "lucide-react";
import { fetchCategorySettings, updateCategorySettings } from "../system/settingsApi";

export const NotificationSettingsPage: React.FC = () => {
  const [notif, setNotif] = useState({
    admission_alert: true,
    fee_reminder: true,
    attendance_warning: true,
    exam_schedule_publish: true,
    result_declaration: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchCategorySettings("notification");
      if (res.data) {
        setNotif((prev) => ({ ...prev, ...res.data }));
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load notification settings.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      await updateCategorySettings("notification", notif);
      setSaved(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to save notification settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-400 text-xs">
        <RefreshCw className="w-5 h-5 animate-spin mr-2 text-indigo-400" />
        <span>Loading notification trigger settings...</span>
      </div>
    );
  }

  return (
    <div className="p-5 bg-slate-900/90 border border-slate-800 rounded-xl shadow-md text-xs font-sans space-y-4">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-950/60 border border-red-800 text-red-300 rounded-lg text-xs">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-center justify-between pb-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-indigo-400" />
          <h2 className="text-base font-bold text-slate-100">Notification Event Triggers & Rules</h2>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold rounded-lg shadow-md transition-colors text-xs"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin text-white" />
              <span>Saving...</span>
            </>
          ) : saved ? (
            <>
              <CheckCircle2 className="w-4 h-4 text-white" />
              <span>Saved!</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              <span>Save Rules</span>
            </>
          )}
        </button>
      </div>

      <div className="space-y-3 font-mono">
        <div className="flex items-center justify-between p-3.5 bg-slate-950 border border-slate-800 rounded-xl">
          <div>
            <h4 className="font-bold text-slate-100 font-sans">Admission Application Stage Alerts</h4>
            <p className="text-slate-400 text-[11px] font-sans">Notify applicants on status changes (verified, approved, admitted).</p>
          </div>
          <input
            type="checkbox"
            checked={notif.admission_alert}
            onChange={(e) => {
              setSaved(false);
              setNotif({ ...notif, admission_alert: e.target.checked });
            }}
            className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-indigo-600 focus:ring-indigo-500"
          />
        </div>

        <div className="flex items-center justify-between p-3.5 bg-slate-950 border border-slate-800 rounded-xl">
          <div>
            <h4 className="font-bold text-slate-100 font-sans">Fee Payment Due Reminders</h4>
            <p className="text-slate-400 text-[11px] font-sans">Automated reminders for outstanding student fee installments.</p>
          </div>
          <input
            type="checkbox"
            checked={notif.fee_reminder}
            onChange={(e) => {
              setSaved(false);
              setNotif({ ...notif, fee_reminder: e.target.checked });
            }}
            className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-indigo-600 focus:ring-indigo-500"
          />
        </div>

        <div className="flex items-center justify-between p-3.5 bg-slate-950 border border-slate-800 rounded-xl">
          <div>
            <h4 className="font-bold text-slate-100 font-sans">Low Attendance Warnings (&lt;75%)</h4>
            <p className="text-slate-400 text-[11px] font-sans">Alert students & parents when attendance drops below threshold.</p>
          </div>
          <input
            type="checkbox"
            checked={notif.attendance_warning}
            onChange={(e) => {
              setSaved(false);
              setNotif({ ...notif, attendance_warning: e.target.checked });
            }}
            className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-indigo-600 focus:ring-indigo-500"
          />
        </div>

        <div className="flex items-center justify-between p-3.5 bg-slate-950 border border-slate-800 rounded-xl">
          <div>
            <h4 className="font-bold text-slate-100 font-sans">Result Declaration Alerts</h4>
            <p className="text-slate-400 text-[11px] font-sans">Notify students instantly when semester marks are published.</p>
          </div>
          <input
            type="checkbox"
            checked={notif.result_declaration}
            onChange={(e) => {
              setSaved(false);
              setNotif({ ...notif, result_declaration: e.target.checked });
            }}
            className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-indigo-600 focus:ring-indigo-500"
          />
        </div>
      </div>
    </div>
  );
};
